import { VISIT_DECISION_TIMEOUT_MS, type VisitOutcome } from "@/platform/app-mode/visit"
import { VISIT_DECISIONS, type VisitAction, type VisitDecision } from "@/platform/bridge/types"

export interface VisitNegotiator {
  readonly propose: (url: string, action: VisitAction) => Promise<VisitOutcome>
  readonly settle: (data: Record<string, unknown>) => void
  readonly dispose: () => void
}

interface Waiting {
  readonly resolve: (outcome: VisitOutcome) => void
  readonly timer: ReturnType<typeof setTimeout>
}

const readDecision = (data: Record<string, unknown>): { url: string; decision: VisitDecision } | null => {
  const { url, decision } = data
  if (typeof url !== "string" || url.length === 0) return null
  if (!(VISIT_DECISIONS as ReadonlyArray<unknown>).includes(decision)) return null

  return { url, decision: decision as VisitDecision }
}

export const createVisitNegotiator = (post: (url: string, action: VisitAction) => void, timeoutMs: number = VISIT_DECISION_TIMEOUT_MS): VisitNegotiator => {
  const waiting = new Map<string, Set<Waiting>>()

  const drop = (url: string, entry: Waiting): void => {
    const entries = waiting.get(url)
    if (entries === undefined) return

    entries.delete(entry)
    if (entries.size === 0) waiting.delete(url)
  }

  return {
    propose: (url, action) =>
      new Promise<VisitOutcome>((resolve) => {
        const entry: Waiting = {
          resolve,
          timer: setTimeout(() => {
            drop(url, entry)
            resolve("timeout")
          }, timeoutMs),
        }

        const entries = waiting.get(url) ?? new Set<Waiting>()
        entries.add(entry)
        waiting.set(url, entries)

        post(url, action)
      }),
    settle: (data) => {
      const answer = readDecision(data)
      if (answer === null) return

      const entries = waiting.get(answer.url)
      if (entries === undefined) return

      waiting.delete(answer.url)
      for (const entry of entries) {
        clearTimeout(entry.timer)
        entry.resolve(answer.decision)
      }
    },
    dispose: () => {
      for (const entries of waiting.values()) {
        for (const entry of entries) {
          clearTimeout(entry.timer)
          entry.resolve("timeout")
        }
      }
      waiting.clear()
    },
  }
}
